/**
 * The admin-action catalog. Every operator write the console can trigger is a
 * typed {@link AdminActionDef}. Availability is grounded in the actual backend
 * surface (see .context/recon):
 *   - "available": an admin token works today (owner-or-admin endpoints).
 *   - "a2-pending": the domain-service method exists but no admin-callable HTTP
 *     route is exposed yet (Workstream A2). Rendered disabled with a note.
 *   - "forbidden": impossible by design (self-custody) — e.g. wallet migration.
 *
 * `requiresReason` adds a mandatory free-text reason to the confirm dialog (for
 * the operator's own audit hygiene; the backend records the actor regardless).
 */
import { adminRequest } from "./client";

export type ActionAvailability = "available" | "a2-pending" | "forbidden";

export interface AdminActionDef<P> {
  key: string;
  /** Button label / verb, e.g. "Revoke". */
  verb: string;
  /** Short sentence describing the effect, shown in the confirm dialog. */
  description: string;
  destructive: boolean;
  requiresReason: boolean;
  availability: ActionAvailability;
  /** Why it's disabled (a2-pending / forbidden). */
  unavailableNote?: string;
  /**
   * Sends the request. Present only when `availability === "available"`. `reason` is the text
   * the operator typed in the confirm dialog (trimmed; `undefined` when the action does not
   * require one) — actions whose backend route records a reason pass it through in the body.
   */
  run?: (token: string, params: P, reason?: string) => Promise<unknown>;
  /**
   * Success toast text built from the response, for actions whose outcome the operator needs to
   * read (e.g. "nothing changed"). Defaults to a generic "will appear in a moment".
   */
  successMessage?: (result: unknown) => string;
}

function available<P>(
  def: Omit<AdminActionDef<P>, "availability"> & {
    run: (token: string, params: P, reason?: string) => Promise<unknown>;
  },
): AdminActionDef<P> {
  return { availability: "available", ...def };
}

// ───────────────────────── Session keys (available now) ─────────────────────────

export const revokeSessionKey = available<{ id: string }>({
  key: "sessionKey.revoke",
  verb: "Revoke",
  description: "Revoke this session key. It's revoked on-chain and can no longer sign operations.",
  destructive: true,
  requiresReason: true,
  run: (token, { id }) =>
    adminRequest({ method: "POST", path: `/sessionKeys/${id}/revoke`, token }),
});

export const requestSessionKeyInstall = available<{ id: string }>({
  key: "sessionKey.installRequest",
  verb: "Re-prompt install",
  description:
    "Ask the user's device to sign and enable (install) this pending session key on-chain. Re-raises a fresh prompt even if a previous one expired or was dismissed.",
  destructive: false,
  requiresReason: false,
  run: (token, { id }) =>
    adminRequest({ method: "POST", path: `/sessionKeys/${id}/install/request`, token }),
});

// ───────────────────────── Wallet (available now) ─────────────────────────

export const provisionWallet = available<{ ownerId: string }>({
  key: "wallet.provision",
  verb: "Provision wallet",
  description: "Provision the user's smart accounts (Main + Reserve) so the wallet becomes active.",
  destructive: false,
  requiresReason: false,
  run: (token, { ownerId }) =>
    adminRequest({ method: "POST", path: `/wallets/byOwner/${ownerId}/provision`, token }),
});

// ───────────────────────── Autonomous backend keys (available now) ─────────────────────────
// The keys the backend uses to act on a wallet without an owner prompt at action time:
// `reserve-top-up` (Main → Reserve) and `cctp-settlement` (Reserve → Rain). Backed by
// @RequireAdminRole() endpoints under /wallets. The default install is idempotent and — because the
// backend now attempt-keys install OwnerOperations — re-raises a FRESH signing prompt for a pending
// key whose previous prompt the user dismissed or let expire. That is the re-attempt path.

/** Autonomous backend session-key types the operator can (re-)install. */
export const AUTONOMOUS_KEY_TYPES = [
  "reserve-top-up",
  "cctp-settlement",
  "reserve-release",
] as const;
export type AutonomousKeyType = (typeof AUTONOMOUS_KEY_TYPES)[number];

export const ensureAutonomousKey = available<{
  walletId: string;
  type: AutonomousKeyType;
}>({
  key: "wallet.ensureAutonomousKey",
  verb: "Install / re-prompt key",
  description:
    "Install this autonomous backend key on the wallet, or re-raise the signing prompt if it's pending (e.g. the user dismissed it or it expired). Idempotent — safe to run repeatedly; no-op once enabled.",
  destructive: false,
  requiresReason: false,
  run: (token, { walletId, type }) =>
    adminRequest({
      method: "POST",
      path: `/wallets/${walletId}/autonomousKeys/${type}/install`,
      token,
    }),
});

export const forceReprovisionAutonomousKey = available<{
  walletId: string;
  type: AutonomousKeyType;
}>({
  key: "wallet.forceReprovisionAutonomousKey",
  verb: "Force re-provision",
  description:
    "Mint a brand-new key (fresh id) and request its install. Use when the current key is REVOKED — a normal install can't reuse a revoked key's id. No-op if a key is already enabled.",
  destructive: true,
  requiresReason: true,
  run: (token, { walletId, type }) =>
    adminRequest({
      method: "POST",
      path: `/wallets/${walletId}/autonomousKeys/${type}/install`,
      query: { force: true },
      token,
    }),
});

export const backfillAutonomousKeysDryRun = available<{ types?: string[] }>({
  key: "wallet.backfillAutonomousKeys.dryRun",
  verb: "Dry run",
  description:
    "Scan provisioned wallets and report how many are missing an enabled autonomous backend key. Prompts no one — report only.",
  destructive: false,
  requiresReason: false,
  run: (token, { types }) =>
    adminRequest({
      method: "POST",
      path: `/wallets/autonomousKeys/backfill`,
      body: { types, dryRun: true },
      token,
    }),
});

export const backfillAutonomousKeysInstall = available<{ types?: string[] }>({
  key: "wallet.backfillAutonomousKeys.install",
  verb: "Install all missing",
  description:
    "Install autonomous backend keys on every provisioned wallet missing one. This raises a signing prompt on each affected user's device — run the dry run first.",
  destructive: true,
  requiresReason: true,
  run: (token, { types }) =>
    adminRequest({
      method: "POST",
      path: `/wallets/autonomousKeys/backfill`,
      body: { types, dryRun: false },
      token,
    }),
});

// ───────────────────────── User role management (available now) ─────────────────────────

export const setUserRoles = available<{ userId: string; roles: string[]; updatedAt: string }>({
  key: "user.setRoles",
  verb: "Update roles",
  description:
    "Set this user's roles. Granting 'admin' makes them an operator — it takes effect on their next sign-in. Recorded against your account.",
  destructive: true,
  requiresReason: true,
  run: (token, { userId, roles, updatedAt }) =>
    adminRequest({
      method: "PATCH",
      path: `/users/${userId}`,
      query: { updatedAt },
      body: { roles },
      token,
    }),
});

// ───────────────────────── Holds (available now) ─────────────────────────

export const expireHold = available<{ id: string; updatedAt: string }>({
  key: "hold.expire",
  verb: "Expire hold",
  description: "Expire this active hold and release the held funds.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }) =>
    adminRequest({
      method: "POST",
      path: `/wallets/holds/${id}/expire`,
      query: { updatedAt },
      token,
    }),
});

export const failHold = available<{ id: string; updatedAt: string }>({
  key: "hold.fail",
  verb: "Fail hold",
  description:
    "Terminalise a hold whose operation did NOT happen (e.g. one left pending by a UserOp submission that never reached the bundler). Releases the encumbrance and records your reason on the row. Does not re-drive anything — when the hold brackets a quarantined smart-account operation, resolve that operation instead so its leg / settlement is re-staged.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }, reason) =>
    adminRequest({
      method: "POST",
      path: `/wallets/holds/${id}/fail`,
      body: { reason, updatedAt },
      token,
    }),
});

export const releaseHold = available<{ id: string; updatedAt: string }>({
  key: "hold.release",
  verb: "Release hold",
  description:
    "Release a hold whose operation DID complete but whose release never fired. Ends the encumbrance only; the operation's own state is untouched. Using this on a hold whose UserOp is still in flight un-encumbers balance the account is about to spend.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }, reason) =>
    adminRequest({
      method: "POST",
      path: `/wallets/holds/${id}/release`,
      body: { reason, updatedAt },
      token,
    }),
});

export const sweepExpiredHolds = available<Record<string, never>>({
  key: "hold.sweepExpired",
  verb: "Sweep expired holds",
  description: "Expire every active hold whose expiry time has already passed.",
  destructive: true,
  requiresReason: true,
  run: (token) =>
    adminRequest({ method: "POST", path: `/wallets/holds/sweepExpired`, body: {}, token }),
});

// ───────────────────────── Smart-account operations (quarantine) ─────────────────────────

export const resolveSmartAccountOperation = available<{ id: string; updatedAt: string }>({
  key: "smartAccountOperation.resolve",
  verb: "Resolve with chain evidence",
  description:
    "Resolve this quarantined (submissionUnknown) operation from what the chain proves. A bundler receipt for its recorded hash closes it as included / reverted; a dispatch that provably never reached the bundler (paymaster or gas-estimation failure, or no receipt after the 7-day window with no matching transfer) is abandoned — its hold is failed and a card leg or settlement attempt is re-staged automatically. Refused (409) when the evidence is inconclusive.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }, reason) =>
    adminRequest({
      method: "POST",
      path: `/wallets/smartAccountOperations/${id}/resolve`,
      body: { reason, updatedAt },
      token,
    }),
});

export const forceAbandonSmartAccountOperation = available<{ id: string; updatedAt: string }>({
  key: "smartAccountOperation.forceAbandon",
  verb: "Force abandon",
  description:
    "Abandon this quarantined operation on your authority when the evidence is inconclusive — only after verifying on-chain that it cannot land (e.g. its nonce slot was consumed by another operation and no matching transfer exists). Fails the operation and its hold; a card leg or settlement attempt is re-staged. Still refused if the ledger shows a matching debit. Put the chain evidence in the reason.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }, reason) =>
    adminRequest({
      method: "POST",
      path: `/wallets/smartAccountOperations/${id}/resolve`,
      body: { reason, updatedAt, force: true },
      token,
    }),
});

// ───────────────────────── Payments (available now) ─────────────────────────

export const rescheduleSettlement = available<{ paymentId: string }>({
  key: "payment.rescheduleSettlement",
  verb: "Reschedule settlement",
  description:
    "Re-stage the still-owed Main→Reserve settlement amount after a transfer failed. Safe to run more than once; does nothing if nothing is owed.",
  destructive: false,
  requiresReason: false,
  run: (token, { paymentId }) =>
    adminRequest({ method: "POST", path: `/payments/${paymentId}/rescheduleSettlement`, token }),
});

export const markPaymentCaptured = available<{
  id: string;
  capturedAt: string;
  finalAmountMicroUsdc: number;
  updatedAt: string;
}>({
  key: "payment.markCaptured",
  verb: "Mark captured",
  description: "Force this approved payment to captured, with a final amount.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, capturedAt, finalAmountMicroUsdc, updatedAt }) =>
    adminRequest({
      method: "POST",
      path: `/payments/${id}/markCaptured`,
      query: { updatedAt },
      body: { capturedAt, finalAmountMicroUsdc },
      token,
    }),
});

export const markPaymentExpired = available<{ id: string; updatedAt: string }>({
  key: "payment.markExpired",
  verb: "Mark expired",
  description: "Force this approved payment to expired.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }) =>
    adminRequest({
      method: "POST",
      path: `/payments/${id}/markExpired`,
      query: { updatedAt },
      token,
    }),
});

export const markPaymentDeclined = available<{
  id: string;
  declinedReasonCode: string;
  reason: string;
  updatedAt: string;
}>({
  key: "payment.markDeclined",
  verb: "Mark declined",
  description: "Force this approved payment to declined, with a reason.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, declinedReasonCode, reason, updatedAt }) =>
    adminRequest({
      method: "POST",
      path: `/payments/${id}/markDeclined`,
      query: { updatedAt },
      body: { declinedReasonCode, reason },
      token,
    }),
});

export const markPaymentReversed = available<{ id: string; updatedAt: string }>({
  key: "payment.markReversed",
  verb: "Mark reversed",
  description: "Force this captured payment to reversed.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }) =>
    adminRequest({
      method: "POST",
      path: `/payments/${id}/markReversed`,
      query: { updatedAt },
      token,
    }),
});

// ───────────────────────── Cards (available now — admin bypass) ─────────────────────────
// The /cards/:id/{freeze,unfreeze,terminate} routes now allow a platform admin to act on
// ANY card (admin bypass added in the cards service); PAN/PIN reveal stays owner-only.

export const adminFreezeCard = available<{ id: string; updatedAt: string }>({
  key: "card.freeze",
  verb: "Freeze card",
  description: "Freeze this card (active → frozen).",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }) =>
    adminRequest({ method: "POST", path: `/cards/${id}/freeze`, query: { updatedAt }, token }),
});

export const adminUnfreezeCard = available<{ id: string; updatedAt: string }>({
  key: "card.unfreeze",
  verb: "Unfreeze card",
  description: "Unfreeze this card (frozen → active).",
  destructive: false,
  requiresReason: false,
  run: (token, { id, updatedAt }) =>
    adminRequest({ method: "POST", path: `/cards/${id}/unfreeze`, query: { updatedAt }, token }),
});

export const adminTerminateCard = available<{ id: string; updatedAt: string }>({
  key: "card.terminate",
  verb: "Terminate card",
  description: "Permanently terminate this card.",
  destructive: true,
  requiresReason: true,
  run: (token, { id, updatedAt }) =>
    adminRequest({ method: "POST", path: `/cards/${id}/terminate`, query: { updatedAt }, token }),
});

// ───────────────────────── KYC (available now — admin only) ─────────────────────────
// DELETE /users/:userId/kycVerifications is admin-gated. It deletes the user's Rain identity
// user(s) (which also cancels their Rain cards) and hard-deletes all their KYC verifications,
// clearing the in-flight/approved gate so KYC can be re-run from scratch. Operator / test tool.

/** Response of `POST /kycVerifications/:id/providerSync`. */
export interface KycProviderSyncResult {
  /** The provider's own status string as read during the sync (for Rain, `applicationStatus`). */
  providerStatus: string;
  /** Whether a local transition was committed. */
  applied: boolean;
  verification: { state: string };
}

// POST /kycVerifications/:id/providerSync (identity-service ≥ 0.12.0, admin-gated). Re-reads the
// provider and applies its current status through the same rules as the webhook: an in-flight row
// moves forward, and an approval overturns a rejected / requiresResubmit row (unless the user is
// already approved on another verification). A provider withdrawing an approval is reported but
// never applied. The repair for a decision whose webhook was lost or dropped (ELS-137).
export const syncKycVerification = available<{ id: string }>({
  key: "kycVerification.providerSync",
  verb: "Re-sync from provider",
  description:
    "Ask the KYC provider (Rain) for this verification's current status and apply it — e.g. a decision whose webhook was lost, or a denial Rain later reversed to approved. Nothing is deleted: if Rain withdrew an approval, that is reported but never applied.",
  destructive: false,
  requiresReason: false,
  run: (token, { id }) =>
    adminRequest<KycProviderSyncResult>({
      method: "POST",
      path: `/kycVerifications/${id}/providerSync`,
      token,
    }),
  successMessage: (result) => {
    const { providerStatus, applied, verification } = result as KycProviderSyncResult;
    return applied
      ? `Provider reports “${providerStatus}” — verification is now ${verification.state}.`
      : `Provider reports “${providerStatus}” — no change; verification stays ${verification.state}.`;
  },
});

export const grantKycRetry = available<{ id: string; updatedAt: string }>({
  key: "kycVerification.grantRetry",
  verb: "Allow one more KYC attempt",
  description:
    "After reviewing this final denial, allow one new paid KYC application, including duplicate, AML or fraud denials. Record why another attempt is appropriate. This preserves existing applications and cards. The user must start the new application; repeating this action does not add more attempts. If the decision changes, review it again.",
  destructive: false,
  requiresReason: true,
  run: (token, { id, updatedAt }, reason) =>
    adminRequest({
      method: "POST",
      path: `/kycVerifications/${id}/retryGrant`,
      token,
      query: { updatedAt },
      body: { reason },
    }),
  successMessage: () =>
    "One attempt is available. The user can start it in the app. No new application has been submitted yet.",
});

export const resetKycUser = available<{ userId: string }>({
  key: "kycVerification.reset",
  verb: "Reset KYC",
  description:
    "Delete this user's Rain identity user (which also cancels their Rain cards), remove all their KYC verifications, terminate their local cards, and clear their cards KYC status — so they can run identity verification from scratch. Irreversible; intended for testing. Never use it to fix a decision that is out of date with Rain — open the verification and use “Re-sync from provider” instead.",
  destructive: true,
  requiresReason: true,
  run: async (token, { userId }) => {
    // Identity first: deletes the Rain user (which cancels the Rain-side cards) and
    // hard-deletes the KYC verifications, clearing the in-flight/approved gate.
    await adminRequest({ method: "DELETE", path: `/users/${userId}/kycVerifications`, token });
    // Then cards: terminate the local Card rows + clear the cards-domain KycStatus projection.
    await adminRequest({ method: "DELETE", path: `/cards/byOwner/${userId}`, token });
  },
});

// ───────────────────────── Owner-key backup (metadata only) ─────────────────────────
// The console reads the metadata projection. Recovery material remains encrypted under the
// user's password, which Elios never receives. There is no admin decrypt or backup-delete route.
// Owner removal and the account-deletion lifecycle are enforced by the backend; retained
// database history/backups may temporarily contain encrypted copies after removal.

/**
 * Permanent tombstone. This is not "not built yet" — it is not buildable.
 *
 * Kept in the catalog, and rendered disabled with this note, so the impossibility is stated on
 * the surface where someone would otherwise go looking for the missing button.
 */
export const supportRecoverOwnerKeyBackup: AdminActionDef<{ keyScope: string }> = {
  key: "ownerKeyBackup.supportRecover",
  verb: "Recover for user",
  description: "Decrypt and reveal the user's recovery phrase.",
  destructive: true,
  requiresReason: false,
  availability: "forbidden",
  unavailableNote:
    "Impossible by design. We hold only ciphertext, encrypted under a password the user chose " +
    "and that we never receive. No key exists on our side to decrypt it — not for support, not " +
    "for an operator, not under subpoena. If the user has lost both their device ecosystem and " +
    "their password, the phrase is gone. Do not open a ticket asking for this to be built.",
};

// ───────────────────────── Forbidden by design ─────────────────────────

export const initiateMigration: AdminActionDef<{ walletId: string }> = {
  key: "wallet.migrate",
  verb: "Initiate migration",
  description: "Initiate a wallet migration.",
  destructive: true,
  requiresReason: false,
  availability: "forbidden",
  unavailableNote:
    "Not possible here. A wallet migration can only be started from the user's own device — the signing key never leaves it.",
};

/** Decline reason codes accepted by PaymentService.markDeclined. */
export const PAYMENT_DECLINED_REASONS = [
  "cardFrozen",
  "cardLocked",
  "cardNotActive",
  "cardTerminated",
  "cardUnknown",
  "fxQuoteUnavailable",
  "insufficientCollateral",
  "kycRevoked",
  "providerInitiatedDecline",
  "unknownDecisionError",
] as const;
